import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

// Birthdates are collected as three selects rather than <input type="date">:
// iOS in-app browsers (WKWebView) often render a calendar whose month/year jump
// is inert, leaving users to swipe back one month at a time to the 1990s.
const YEARS_BACK = 120;

type DobParts = { day: string; month: string; year: string };

const EMPTY: DobParts = { day: "", month: "", year: "" };

const splitValue = (value: string): DobParts => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || "");
  return match ? { year: match[1], month: match[2], day: match[3] } : EMPTY;
};

const daysInMonth = (year: string, month: string) =>
  year && month ? new Date(Number(year), Number(month), 0).getDate() : 31;

const DobField = ({ input, meta, id }: any) => {
  const { t, i18n } = useTranslation();
  const [parts, setParts] = useState<DobParts>(() => splitValue(input.value));
  const lastEmitted = useRef<string | null>(null);

  // Sync when the value is reset from outside (redux-form reinitializing the
  // wizard step), but ignore the echo of our own onChange so a partial
  // selection isn't wiped while the user is still picking.
  useEffect(() => {
    if (input.value === lastEmitted.current) return;
    setParts(splitValue(input.value));
  }, [input.value]);

  const months = useMemo(() => {
    const formatter = new Intl.DateTimeFormat(i18n.language || "en", { month: "short" });
    return Array.from({ length: 12 }, (_, index) => ({
      value: String(index + 1).padStart(2, "0"),
      label: formatter.format(new Date(2000, index, 1)),
    }));
  }, [i18n.language]);

  const years = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: YEARS_BACK + 1 }, (_, index) => String(currentYear - index));
  }, []);

  const days = useMemo(
    () =>
      Array.from({ length: daysInMonth(parts.year, parts.month) }, (_, index) =>
        String(index + 1).padStart(2, "0")
      ),
    [parts.year, parts.month]
  );

  const handleChange = (key: keyof DobParts) => (event: React.ChangeEvent<HTMLSelectElement>) => {
    const next = { ...parts, [key]: event.target.value };
    if (Number(next.day) > daysInMonth(next.year, next.month)) {
      next.day = "";
    }
    setParts(next);

    const combined = next.year && next.month && next.day
      ? `${next.year}-${next.month}-${next.day}`
      : "";
    lastEmitted.current = combined;
    input.onChange(combined);
  };

  const invalid = meta.touched && meta.error;
  const selectClass = `form-select rounded-5 py-2 ${invalid ? "border-danger" : ""}`;

  return (
    <div className="mb-1">
      <div className="row g-2">
        <div className="col-3">
          <select
            id={id}
            className={selectClass}
            value={parts.day}
            onChange={handleChange("day")}
            onBlur={() => input.onBlur(input.value)}
            aria-label={t("register.dob_day")}
          >
            <option value="">{t("register.dob_day")}</option>
            {days.map((day) => (
              <option key={day} value={day}>
                {Number(day)}
              </option>
            ))}
          </select>
        </div>
        <div className="col-5">
          <select
            className={selectClass}
            value={parts.month}
            onChange={handleChange("month")}
            onBlur={() => input.onBlur(input.value)}
            aria-label={t("register.dob_month")}
          >
            <option value="">{t("register.dob_month")}</option>
            {months.map((month) => (
              <option key={month.value} value={month.value}>
                {month.label}
              </option>
            ))}
          </select>
        </div>
        <div className="col-4">
          <select
            className={selectClass}
            value={parts.year}
            onChange={handleChange("year")}
            onBlur={() => input.onBlur(input.value)}
            aria-label={t("register.dob_year")}
          >
            <option value="">{t("register.dob_year")}</option>
            {years.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
      </div>
      {invalid && (
        <p className="text-danger small mt-1 ps-2 mb-0 fw-bold">{meta.error}</p>
      )}
    </div>
  );
};

export default DobField;
